import { useMemo } from "react";

import { Circle, Flex, Box } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router";

import { TaskMetadataReason, TaskType } from "__graphql__/types";
import { useAppBackgroundEffectsStore } from "app/stores/useAppBackgroundEffectsStore";
import { EppoFeatureFlags } from "app/types/flags";
import { routes } from "config/routes";
import { STOCK_CHECK_METADATA_REASONS } from "flows/Inventory/flows/StockChecks/constants";
import { useGetOpenShelfChangeProposalsCountQuery } from "flows/ShelfChanges/queries/shelfChanges.generated";
import { Page } from "shared/components/Page";
import { useEppoFeatureFlagProvider } from "shared/hooks/useEppoFeatureFlag";
import { IntlMessageId } from "shared/lang";
import { useGetPendingCheckCountQuery } from "shared/queries/checkCount/checkCount.generated";
import {
  CaretRightIcon,
  InboundItemsIcon,
  StockCorrectionIcon,
  RescueBagIcon,
  BatteryChargeIcon,
  ShelfIcon,
  CalendarDepartureIcon,
  CoinGrowthIcon,
  DeviceIcon,
  AppleCheckIcon,
  HierarchyIcon,
  CalendarWarningIcon,
  PartyConfettiIcon,
  BackwardIcon,
  DeliveryPackagesIcon,
} from "ui/Icons/Icons";
import { BodyS, TitleM, TitleXS } from "ui/Typography/Typography";
import { isNotNullNorUndefined } from "utils/tsHelpers";

import { getInventoryPeriodKey } from "../utils/inventoryChecks";

type InventoryTaskProps = {
  label: string;
  count?: number;
  dataTestId?: string;
  icon?: React.ReactNode;
  onClick: () => void;
};

const PINNED_NAVIGATION_ITEMS = ["restockingList", "stockCorrection"];

const SUPPORT_TASKS = [
  "batteryManagement",
  "tooGoodToGo",
  "shelfChanges",
  "batteryCount",
  "bbdCorrection",
];

function CircularInventoryTask({ label, count, dataTestId, icon, onClick }: InventoryTaskProps) {
  return (
    <Flex
      maxW="150px"
      direction="column"
      alignItems="center"
      data-testid={dataTestId}
      onClick={onClick}
      cursor="pointer"
    >
      <Box position="relative">
        <Circle size="56px" bg="grey.150">
          {icon}
        </Circle>
        {isNotNullNorUndefined(count) && (
          <Circle size="22px" bg="pinkFlink.500" position="absolute" top="-5px" right="-5px">
            <TitleXS color="white">{count}</TitleXS>
          </Circle>
        )}
      </Box>
      <BodyS textAlign="center">{label}</BodyS>
    </Flex>
  );
}

function BoxedInventoryTask({
  label,
  dataTestId,
  icon,
  onClick,
}: Omit<InventoryTaskProps, "count">) {
  return (
    <Flex
      w="100px"
      h="100px"
      direction="column"
      justifyContent="center"
      alignItems="flex-start"
      data-testid={dataTestId}
      onClick={onClick}
      cursor="pointer"
      bg="white"
      borderBottom="1px solid"
      borderBottomColor="grey.200"
      p="s200"
      gap="s100"
      overflow="hidden"
    >
      {icon}
      <BodyS noOfLines={2} maxW="68px">
        {label}
      </BodyS>
    </Flex>
  );
}

function ListInventoryTask({ label, count, dataTestId, icon, onClick }: InventoryTaskProps) {
  return (
    <Flex
      w="100%"
      alignItems="center"
      p="s200"
      bg="white"
      borderBottom="1px solid"
      borderBottomColor="grey.200"
      data-testid={dataTestId}
      onClick={onClick}
    >
      {icon}
      <BodyS ml="s150">{label}</BodyS>
      {isNotNullNorUndefined(count) && (
        <Circle minW="20px" minH="20px" px="s50" bg="grey.600" ml="auto">
          <TitleXS color="white">{count}</TitleXS>
        </Circle>
      )}
      <CaretRightIcon boxSize="1.5rem" ml="s150" />
    </Flex>
  );
}

export function InventoryPage() {
  const intl = useIntl();
  const navigate = useNavigate();

  const { isFeatureEnabled: showEoyChecks } = useEppoFeatureFlagProvider(
    EppoFeatureFlags.EOY_SHOW_CHECKS,
  );
  const { isFeatureEnabled: isEOYMode } = useEppoFeatureFlagProvider(
    EppoFeatureFlags.EOY_MODE_ENABLED,
  );
  const { isFeatureEnabled: isEOYHideCorrectionsEnabled } = useEppoFeatureFlagProvider(
    EppoFeatureFlags.EOY_HIDE_CORRECTIONS,
  );
  const { isFeatureEnabled: isShelfChangesEnabled } = useEppoFeatureFlagProvider(
    EppoFeatureFlags.ENABLE_SHELF_CHANGE_ASSISTANT,
  );
  const publicRestockingItemCount = useAppBackgroundEffectsStore(
    (state) => state.publicRestockingItemCount,
  );
  const { data: freshCheckCountData } = useGetPendingCheckCountQuery({
    fetchPolicy: "network-only",
    variables: { input: { type: [TaskType.freshness_check] } },
  });

  const pendingFreshChecks = freshCheckCountData?.getOpenedChecksCountForHub.checkCount ?? 0;

  const { data: bbdCheckCountData } = useGetPendingCheckCountQuery({
    fetchPolicy: "network-only",
    variables: { input: { type: [TaskType.bbd_check] } },
  });

  const pendingBbdChecks = bbdCheckCountData?.getOpenedChecksCountForHub.checkCount ?? 0;

  const { data: stockCheckCountData } = useGetPendingCheckCountQuery({
    fetchPolicy: "network-only",
    variables: {
      input: {
        type: [TaskType.stock_check],
        metadataReason: STOCK_CHECK_METADATA_REASONS,
      },
    },
  });

  const pendingStockChecks = stockCheckCountData?.getOpenedChecksCountForHub.checkCount ?? 0;

  const { data: rollingInventoryCheckCountData } = useGetPendingCheckCountQuery({
    fetchPolicy: "network-only",
    variables: {
      input: {
        type: [TaskType.stock_check],
        metadataReason: [TaskMetadataReason.rolling_inventory],
      },
    },
  });

  const pendingRollingInventoryChecks =
    rollingInventoryCheckCountData?.getOpenedChecksCountForHub.checkCount ?? 0;

  const { data: inboundDeviationCheckCountData } = useGetPendingCheckCountQuery({
    fetchPolicy: "network-only",
    variables: { input: { type: [TaskType.inventory_discrepancy_check] } },
  });

  const { data: openShelfChangeProposalsCountData } = useGetOpenShelfChangeProposalsCountQuery({
    skip: !isShelfChangesEnabled,
    fetchPolicy: "network-only",
  });

  const pendingInboundDeviationChecks =
    inboundDeviationCheckCountData?.getOpenedChecksCountForHub.checkCount ?? 0;

  const pendingOpenShelfChangeProposalsCount =
    openShelfChangeProposalsCountData?.getOpenShelfChangeProposalsCount?.count ?? 0;

  const checksTitle = getInventoryPeriodKey();

  const inventoryItems = useMemo(() => {
    const items = [
      {
        key: "eoyChecks",
        labelId: checksTitle,
        route: routes.inventory.eoyChecks.root,
        featureFlag: showEoyChecks,
        count: undefined,
        dataTestId: "eoy-checks-link",
        icon: <PartyConfettiIcon boxSize="1.5rem" color="grey.800" />,
      },
      {
        key: "recountChecks",
        labelId: "pages.inventory.recount-checks.title",
        route: routes.inventory.eoyRecountChecks.root,
        featureFlag: showEoyChecks,
        count: undefined,
        dataTestId: "recount-checks-link",
        icon: <BackwardIcon boxSize="1.5rem" color="grey.800" />,
      },
      {
        key: "restockingList",
        labelId: "pages.inventory.restocking-list.title",
        route: routes.inventory.restockingList.root,
        featureFlag: !isEOYMode,
        count: publicRestockingItemCount,
        dataTestId: "restocking-list-link",
        icon: <InboundItemsIcon boxSize="1.5rem" color="grey.800" />,
      },
      {
        key: "freshChecks",
        labelId: "pages.inventory.fresh-checks.title",
        route: routes.inventory.freshChecks.root,
        featureFlag: !isEOYMode && pendingFreshChecks > 0,
        count: pendingFreshChecks,
        dataTestId: "fresh-checks-link",
        icon: <AppleCheckIcon boxSize="1.5rem" color="grey.800" />,
      },
      {
        key: "bbdChecks",
        labelId: "pages.inventory.bbd-checks.title",
        route: routes.inventory.bbdChecks.root,
        featureFlag: !isEOYMode && pendingBbdChecks > 0,
        count: pendingBbdChecks,
        dataTestId: "bbd-checks-link",
        icon: <CalendarWarningIcon boxSize="1.5rem" color="grey.800" />,
      },
      {
        key: "bbdCorrection",
        labelId: "pages.inventory.bbd-correction.title",
        route: routes.inventory.bbdCorrections.root,
        featureFlag: !isEOYMode,
        count: undefined,
        dataTestId: "bbd-correction-link",
        icon: <CalendarDepartureIcon boxSize="1.5rem" color="grey.800" />,
      },
      {
        key: "stockChecks",
        labelId: "pages.inventory.stock-checks.title",
        route: routes.inventory.stockChecks.root,
        featureFlag: !isEOYHideCorrectionsEnabled && pendingStockChecks > 0,
        count: pendingStockChecks,
        dataTestId: "stock-checks-link",
        icon: <DeviceIcon boxSize="1.5rem" color="grey.800" />,
      },
      {
        key: "rollingInventoryChecks",
        labelId: "pages.inventory.rolling-inventory-checks.title",
        route: routes.inventory.rollingInventoryChecks.root,
        featureFlag: !isEOYHideCorrectionsEnabled && pendingRollingInventoryChecks > 0,
        count: pendingRollingInventoryChecks,
        dataTestId: "rolling-inventory-checks-link",
        icon: <DeliveryPackagesIcon boxSize="1.5rem" color="grey.800" />,
      },
      {
        key: "stockCorrection",
        labelId: "pages.inventory.stock-correction.title",
        route: routes.inventory.stockCorrections.root,
        featureFlag: !isEOYMode,
        count: undefined,
        dataTestId: "stock-correction-link",
        icon: <StockCorrectionIcon boxSize="1.5rem" color="grey.800" />,
      },
      {
        key: "shelfChanges",
        labelId: "pages.inventory.shelf-changes.title",
        route: routes.inventory.shelfChanges.root,
        featureFlag: isShelfChangesEnabled,
        count: pendingOpenShelfChangeProposalsCount,
        dataTestId: "shelf-changes-link",
        icon: <ShelfIcon boxSize="1.5rem" color="grey.800" />,
      },
      {
        key: "batteryManagement",
        labelId: "pages.inventory.battery-management.title",
        route: routes.inventory.batteryManagement.root,
        featureFlag: true,
        count: undefined,
        dataTestId: "battery-management-link",
        icon: <BatteryChargeIcon boxSize="1.5rem" color="grey.800" />,
      },
      {
        key: "batteryCount",
        labelId: "pages.inventory.battery-count.title",
        route: routes.inventory.batteryCount.root,
        featureFlag: true,
        count: undefined,
        dataTestId: "battery-count-link",
        icon: <CoinGrowthIcon boxSize="1.5rem" color="grey.800" />,
      },
      {
        key: "tooGoodToGo",
        labelId: "pages.inventory.too-good-to-go.title",
        route: routes.inventory.tooGoodToGo.root,
        featureFlag: !isEOYMode,
        count: undefined,
        dataTestId: "too-good-to-go-link",
        icon: <RescueBagIcon boxSize="1.5rem" color="grey.800" />,
      },
      {
        key: "inboundDeviationChecks",
        labelId: "pages.inventory.inbound-deviation-checks.title",
        route: routes.inventory.inboundDeviationChecks.root,
        featureFlag: !isEOYHideCorrectionsEnabled && pendingInboundDeviationChecks > 0,
        count: pendingInboundDeviationChecks,
        dataTestId: "inbound-deviation-checks-link",
        icon: <HierarchyIcon boxSize="1.5rem" color="grey.800" />,
      },
    ];

    return items
      .filter((item) => item.featureFlag)
      .map((item) => ({
        ...item,
        label: intl.formatMessage({ id: item.labelId as IntlMessageId }),
        onClick: () => navigate(item.route),
      }));
  }, [
    checksTitle,
    showEoyChecks,
    isEOYMode,
    publicRestockingItemCount,
    pendingFreshChecks,
    pendingBbdChecks,
    isEOYHideCorrectionsEnabled,
    pendingStockChecks,
    pendingRollingInventoryChecks,
    isShelfChangesEnabled,
    pendingOpenShelfChangeProposalsCount,
    pendingInboundDeviationChecks,
    intl,
    navigate,
  ]);

  const pinnedTasks = inventoryItems.filter((item) => PINNED_NAVIGATION_ITEMS.includes(item.key));
  const openTasks = inventoryItems
    .filter((item) => item.count && item.count > 0 && !PINNED_NAVIGATION_ITEMS.includes(item.key))
    .sort((a, b) => (b.count ?? 0) - (a.count ?? 0));
  const supportTasks = inventoryItems
    .filter((item) => SUPPORT_TASKS.includes(item.key) && (!item.count || item.count === 0))
    .sort((a, b) => SUPPORT_TASKS.indexOf(a.key) - SUPPORT_TASKS.indexOf(b.key));

  return (
    <Page h="full" p="0" isBgGrey>
      <Box h="full" w="full" overflow="auto">
        <TitleM pt="s300" px="s200" pb="s200">
          {intl.formatMessage({ id: "pages.inventory.task-list.pinned.title" })}
        </TitleM>

        <Flex w="full" bg="white" py="s200" px="s100" gap="s300">
          {pinnedTasks.map((task) => (
            <CircularInventoryTask
              key={task.key}
              label={task.label}
              onClick={task.onClick}
              count={task.count}
              icon={task.icon}
              dataTestId={task.dataTestId}
            />
          ))}
        </Flex>

        {openTasks.length > 0 && (
          <>
            <TitleM pt="s300" px="s200" pb="s200">
              {intl.formatMessage({ id: "pages.inventory.task-list.open.title" })}
            </TitleM>

            <Box w="full">
              {openTasks.map((task) => (
                <ListInventoryTask
                  key={task.key}
                  label={task.label}
                  onClick={task.onClick}
                  count={task.count}
                  icon={task.icon}
                  dataTestId={task.dataTestId}
                />
              ))}
            </Box>
          </>
        )}

        <TitleM pt="s300" px="s200" pb="s200">
          {intl.formatMessage({ id: "pages.inventory.task-list.support.title" })}
        </TitleM>
        <Flex w="full" py="s100" px="s100" rowGap="s100" columnGap="s200" flexWrap="wrap">
          {supportTasks.map((task) => (
            <BoxedInventoryTask
              key={task.key}
              label={task.label}
              onClick={task.onClick}
              icon={task.icon}
              dataTestId={task.dataTestId}
            />
          ))}
        </Flex>
      </Box>
    </Page>
  );
}
